<template>
  <v-card class="mt-5">
    <v-card-title>
      {{ $t("LABELS.EXPENSES") }}
      <v-spacer></v-spacer>
      <v-btn color="primary" @click="$emit('add-row')">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("LABELS.ADD_ROW") }}
      </v-btn>
    </v-card-title>

    <table v-if="rows.length > 0" class="rows-table">
      <thead>
        <tr>
          <th class="fuel-type-header">
            {{ $t("MENU.ITEM.FUELS.FUEL_TYPE") }}
          </th>
          <th>{{ $t("FORM_LABELS.QTY") }}</th>
          <th>{{ $t("FORM_LABELS.PRICE") }}</th>
          <th>{{ $t("FORM_LABELS.TOTAL_PRICE") }}</th>
          <th style="width: 100px;">{{ $t("BUTTON.ACTIONS") }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(row, index) in rows">
          <FuelExpenseFormRowItem
            :row="row"
            :fuelTypes="fuelTypes"
            :hasError="hasRowError(index)"
            :hasFuelTypeError="hasFuelTypeError(index)"
            @update-field="$emit('update-row', index, $event, arguments[1])"
            @delete="$emit('delete-row', index)"
          />

          <!-- Tank Charges Sub-rows -->
          <FuelExpenseFormTankChargeItem
            v-for="(charge, chargeIndex) in row.tankCharges"
            :key="
              `charge-${row.id || row.tempId || index}-${charge.id ||
                charge.tempId ||
                chargeIndex}`
            "
            :charge="charge"
            :vehicles="vehicles"
            :staffList="staffList"
            :hasFuelableError="
              hasTankChargeFieldError(index, chargeIndex, 'fuelable')
            "
            @update-field="
              $emit(
                'update-tank-charge',
                index,
                chargeIndex,
                $event,
                arguments[1]
              )
            "
            @delete="$emit('delete-tank-charge', index, chargeIndex)"
          />

          <!-- Add Tank Charge Row -->
          <tr
            class="add-charge-row"
            :class="{ 'error-row': hasTankChargeError(index) }"
          >
            <td colspan="5" class="add-charge-cell">
              <v-btn
                x-small
                text
                :color="hasTankChargeError(index) ? 'error' : 'primary'"
                @click="$emit('add-tank-charge', index)"
              >
                <v-icon small left>mdi-plus</v-icon>
                {{ $t("MENU.ITEM.TANKS.CHARGES") }}
              </v-btn>
              <span v-if="hasTankChargeError(index)" class="error-message ml-2">
                <v-icon small color="error">mdi-alert-circle</v-icon>
                {{
                  $t("ALERTS.TANK_CHARGE_REQUIRED_SHORT") || "Задължително поле"
                }}
              </span>
            </td>
          </tr>
        </template>

        <tr>
          <td colspan="2" class="text-right font-weight-bold">
            {{ $t("LABELS.TOTAL") }}:
          </td>
          <td class="font-weight-bold">
            {{ totalLiters }} {{ $t("LABELS.LITERS") }}
          </td>
          <td class="font-weight-bold">{{ totalSum }} &euro;</td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div v-else class="pa-5 text-center text-muted">
      <v-icon large color="grey lighten-1">mdi-inbox</v-icon>
      <p class="mt-3">
        {{ $t("LABELS.NO_ADDED_ROWS") }}
      </p>
    </div>
  </v-card>
</template>

<script>
import FuelExpenseFormRowItem from "./FuelExpenseFormRowItem.vue";
import FuelExpenseFormTankChargeItem from "./FuelExpenseFormTankChargeItem.vue";

export default {
  name: "FuelExpenseFormRowsList",
  components: {
    FuelExpenseFormRowItem,
    FuelExpenseFormTankChargeItem
  },
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    fuelTypes: {
      type: Array,
      default: () => []
    },
    vehicles: {
      type: Array,
      default: () => []
    },
    staffList: {
      type: Array,
      default: () => []
    },
    rowsWithErrors: {
      type: Array,
      default: () => []
    },
    rowValidationErrors: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    totalSum() {
      if (!this.rows) return "0.00";
      return this.rows
        .reduce((sum, r) => sum + parseFloat(r.totalPrice || 0), 0)
        .toFixed(2);
    },
    totalLiters() {
      if (!this.rows) return "0.00";
      return this.rows
        .reduce((sum, r) => sum + parseFloat(r.totalQty || 0), 0)
        .toFixed(2);
    }
  },
  methods: {
    hasRowError(index) {
      return this.rowsWithErrors.includes(index);
    },
    hasFuelTypeError(index) {
      return this.rowValidationErrors[index]?.fuelTypeId === true;
    },
    hasTankChargeError(index) {
      return this.rowValidationErrors[index]?.tankCharges === true;
    },
    hasTankChargeFieldError(rowIndex, chargeIndex, fieldName) {
      return (
        this.rowValidationErrors[rowIndex]?.tankChargeErrors?.[chargeIndex]?.[
          fieldName
        ] === true
      );
    },
    validateRows() {
      const errors = [];
      const validationErrors = {};

      this.form.rows.forEach((row, index) => {
        const rowErrors = {};

        if (!row.fuelTypeId) {
          rowErrors.fuelTypeId = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (!row.tankCharges || row.tankCharges.length === 0) {
          rowErrors.tankCharges = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        } else {
          const tankChargeErrors = {};
          row.tankCharges.forEach((charge, chargeIndex) => {
            const chargeErrors = {};

            if (
              !charge.fuelable ||
              !charge.fuelableId ||
              !charge.fuelableType ||
              !charge.fuelableName
            ) {
              chargeErrors.fuelable = true;
            }

            const liters = parseFloat(charge.liters);
            if (isNaN(liters) || liters < 0.001) {
              chargeErrors.liters = true;
            }

            if (Object.keys(chargeErrors).length > 0) {
              tankChargeErrors[chargeIndex] = chargeErrors;
              if (!errors.includes(index)) {
                errors.push(index);
              }
            }
          });

          if (Object.keys(tankChargeErrors).length > 0) {
            rowErrors.tankChargeErrors = tankChargeErrors;
          }
        }

        if (Object.keys(rowErrors).length > 0) {
          validationErrors[index] = rowErrors;
        }
      });

      this.$set(this, "rowsWithErrors", errors);
      this.$set(this, "rowValidationErrors", validationErrors);
      return this.rowsWithErrors.length === 0;
    }
  }
};
</script>

<style scoped>
.rows-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.rows-table th,
.rows-table td {
  text-align: left;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.rows-table th {
  background-color: #fafafa;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #666;
}

.rows-table th:first-child,
.rows-table td:first-child {
  padding-left: 24px;
}

.fuel-type-header {
  width: 40%;
}

.add-charge-row {
  background-color: #f8f9fa;
}

.add-charge-row.error-row {
  background-color: #ffe2e5;
}

.add-charge-cell {
  padding: 12px 24px !important;
  text-align: center;
}

.error-message {
  color: #f64e60;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}
</style>
